import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";

// Argon Dashboard 2 MUI example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Argon Dashboard 2 MUI themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Argon Dashboard 2 MUI routes
import routes from "routes";

// Argon Dashboard 2 MUI contexts
import { useArgonController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
// import brand from "assets/images/logo-ct.png";
// import brandDark from "assets/images/logo-ct-dark.png";

import brand from "assets/images/edushala.png";
import brandDark from "assets/images/edushala.png";

// Icon Fonts
import "assets/css/nucleo-icons.css";
import "assets/css/nucleo-svg.css";

import { messaging } from "./firebase/firebase-config";
import { onMessage, getToken } from "firebase/messaging";
import Message from "./components/message";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getCompany } from "services/companyService";

// Permission helper functions
const hasModulePermission = (permissions, moduleName) => {
  // Super admin check
  if (Array.isArray(permissions) && permissions.includes("ALL")) {
    console.log(`hasModulePermission: ${moduleName} - ALLOWED (Super Admin)`);
    return true;
  }

  // Object-based permissions check
  if (permissions && typeof permissions === "object" && !Array.isArray(permissions)) {
    // Convert STUDENT_LEADER_BOARD -> studentLeaderBoard
    // Keep draftTutorSalary -> draftTutorSalary (no change needed)
    const moduleKey = moduleName.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());

    const module = permissions[moduleKey];

    console.log(`hasModulePermission: ${moduleName} -> ${moduleKey}`, {
      permissions,
      module,
      hasPermission: !!(module && (module.create || module.get || module.update || module.delete)),
    });

    if (!module) {
      return false;
    }

    // Check if at least one operation is allowed
    return module.create || module.get || module.update || module.delete;
  }

  // Legacy array-based permissions (backward compatibility)
  if (Array.isArray(permissions)) {
    return permissions.includes(moduleName) || permissions.includes(moduleName.toLowerCase());
  }

  return false;
};

// Function to refresh permissions from server
const refreshPermissions = async () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      console.log("No token found, cannot refresh permissions");
      return false;
    }

    const response = await fetch(`${process.env.REACT_APP_API}/api/user/profile`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data) {
        const currentPermissions = localStorage.getItem("allowedPermissions");
        const newPermissions = data.data.role === "SUPER_ADMIN" ? ["ALL"] : data.data.permissions;

        // Check if permissions have changed
        const permissionsChanged =
          JSON.stringify(JSON.parse(currentPermissions || "{}")) !== JSON.stringify(newPermissions);

        if (permissionsChanged) {
          console.log("🔄 Permissions changed! Updating...");

          // Update localStorage with fresh user data and permissions
          localStorage.setItem("user", JSON.stringify(data.data));

          if (data.data.role === "SUPER_ADMIN") {
            localStorage.setItem("allowedPermissions", JSON.stringify(["ALL"]));
          } else {
            localStorage.setItem("allowedPermissions", JSON.stringify(data.data.permissions));
          }

          console.log("✅ Permissions refreshed successfully:", newPermissions);

          // Show notification
          toast.info("Your permissions have been updated. Refreshing...", {
            position: "top-center",
            autoClose: 2000,
          });

          // Force re-render by reloading page after 2 seconds
          setTimeout(() => {
            window.location.reload();
          }, 2000);

          return true;
        } else {
          console.log("✓ Permissions unchanged");
        }
      }
    } else {
      console.error("Failed to refresh permissions:", response.status);
    }
  } catch (error) {
    console.error("Error refreshing permissions:", error);
  }
  return false;
};

// Make refreshPermissions available globally
window.refreshPermissions = refreshPermissions;

// Add keyboard shortcut to refresh permissions (Ctrl+Shift+R)
document.addEventListener("keydown", (e) => {
  if (e.ctrlKey && e.shiftKey && e.key === "R") {
    e.preventDefault();
    console.log("Manual permission refresh triggered");
    refreshPermissions();
  }
});
import { useNavigate } from "react-router-dom";

import FingerprintJS from "@fingerprintjs/fingerprintjs";

export default function App() {
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor, darkSidenav, darkMode } =
    controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const [dynamicBrand, setDynamicBrand] = useState(brand);

  // Fetch company logo dynamically
  useEffect(() => {
    const fetchCompanyLogo = async () => {
      try {
        const response = await getCompany();
        if (response.success && response.data && response.data.logo) {
          setDynamicBrand(response.data.logo);
        }
      } catch (error) {
        console.error("Failed to fetch company logo:", error);
        // Keep default brand if fetch fails
      }
    };
    fetchCompanyLogo();
  }, []);

  // Setup foreground notification listener
  useEffect(() => {
    // Foreground notifications
    const unsubscribe = onMessage(messaging, (payload) => {
      // Display toast notification
      toast.success(<Message notification={payload.notification} />, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Play notification sound (optional)
      try {
        const audio = new Audio("/notification-sound.mp3");
        audio.play().catch(() => {});
      } catch (audioError) {
        // Audio not available
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  async function requestPermission() {
    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      try {
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_VAPID_KEY,
        });

        if (!token) {
          return;
        }

        const fp = await FingerprintJS.load();
        const { visitorId } = await fp.get();

        const storedUser = localStorage.getItem("user");
        if (!storedUser) {
          return;
        }

        const parsedUser = (() => {
          try {
            return JSON.parse(storedUser);
          } catch (parseError) {
            return null;
          }
        })();

        const userId = parsedUser?._id || parsedUser?.id;
        if (!userId) {
          return;
        }

        // Register admin token
        const response = await fetch(`${process.env.REACT_APP_API}/api/auth/sendAdminToken`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fcmToken: token,
            deviceId: visitorId,
            userId,
          }),
        });

        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(`Failed to register admin token (${response.status}): ${errorBody}`);
        }

        const data = await response.json();

        // Subscribe to admin_notifications topic for receiving all app notifications
        try {
          const subscribeResponse = await fetch(
            `${process.env.REACT_APP_API}/api/notification/subscribe-to-topic`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                tokens: [token],
                topic: "admin_notifications",
              }),
            }
          );

          if (subscribeResponse.ok) {
            const subscribeData = await subscribeResponse.json();
            toast.success("📢 Admin notifications enabled! You'll receive real-time updates.", {
              position: "top-right",
              autoClose: 3000,
            });
          } else {
            const errorText = await subscribeResponse.text();
          }
        } catch (subscribeError) {}
      } catch (error) {}
    } else if (permission === "denied") {
      alert("You denied for the notification");
    }
  }

  useEffect(() => {
    requestPermission();
  }, []);

  // Permissions auto-refresh polling (every 30 seconds)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    // Start polling for permission changes
    const pollInterval = setInterval(() => {
      refreshPermissions();
    }, 30000); // Check every 30 seconds

    // Also check when user comes back to tab (visibility change)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("👁️ Tab visible again, checking permissions...");
        refreshPermissions();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(pollInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  //token k liye
  const navigate = useNavigate();
  const isToken = localStorage.getItem("token");

  useEffect(() => {
    if (isToken == null) {
      navigate("/authentication/sign-in");
    }
  }, [isToken, navigate]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) => {
    // LocalStorage se permissions le aao (object ya array ho sakta hai)
    const allowedPermissions = JSON.parse(localStorage.getItem("allowedPermissions")) || [];

    return allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        // ✅ Agar route ke liye permission defined hai
        if (route.permission) {
          // Check karo: using helper function
          const hasPermission = route.permission.some((perm) =>
            hasModulePermission(allowedPermissions, perm)
          );

          if (!hasPermission) {
            return null; // Agar permission match nahi hui toh route skip kar do
          }
        }

        // ✅ Agar permission match ho gayi toh route return karo
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });
  };

  // Sidebar ke liye routes filter karne ka function

  const filterRoutes = (allRoutes) => {
    const allowedPermissions = JSON.parse(localStorage.getItem("allowedPermissions")) || [];
    console.log("filterRoutes: allowedPermissions =", allowedPermissions);

    return allRoutes
      .map((route) => {
        // Parent permission check
        const parentHasPermission = route.permission
          ? route.permission.some((perm) => hasModulePermission(allowedPermissions, perm))
          : true; // agar permission define nahi hai to show by default

        console.log(
          `Route ${route.name || route.key}: parentHasPermission =`,
          parentHasPermission,
          "permissions =",
          route.permission
        );

        if (!parentHasPermission) return null;

        // Agar parent ke andar collapse (child routes) hain
        if (route.collapse) {
          const filteredCollapse = route.collapse
            .map((child) => {
              // Child route ke liye permission agar define hai to check karo
              if (child.permission) {
                const childHasPermission = child.permission.some((perm) =>
                  hasModulePermission(allowedPermissions, perm)
                );
                if (!childHasPermission) return null;
              }
              return child;
            })
            .filter(Boolean); // null remove

          // Agar collapse ke andar kuch items allowed hain tabhi parent show karo
          return filteredCollapse.length > 0 ? { ...route, collapse: filteredCollapse } : null;
        }

        return route;
      })
      .filter(Boolean);
  };

  // const filterRoutes = (allRoutes) => {
  //   const allowedPermissions = JSON.parse(localStorage.getItem("allowedPermissions")) || [];

  //   return allRoutes
  //     .map((route) => {
  //       if (route.collapse) {
  //         const filteredCollapse = filterRoutes(route.collapse);
  //         // Agar collapse ke andar bhi kuch items allowed hain, tabhi include karo
  //         return filteredCollapse.length > 0 ? { ...route, collapse: filteredCollapse } : null;
  //       }

  //       if (route.permission) {
  //         const hasPermission =
  //           allowedPermissions.includes("ALL") ||
  //           route.permission.some((perm) => allowedPermissions.includes(perm));

  //         if (!hasPermission) return null;
  //       }

  //       return route; // Agar permission match ho gayi ya permission nahi defined
  //     })
  //     .filter(Boolean); // null values remove kar do
  // };

  // const getRoutes = (allRoutes) =>
  //   allRoutes.map((route) => {
  //     console.log(route);
  //     if (route.collapse) {
  //       return getRoutes(route.collapse);
  //     }
  //     if (route.route) {
  //       return <Route exact path={route.route} element={route.component} key={route.key} />;
  //     }
  //     return null;
  //   });

  const configsButton = (
    <ArgonBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer", display: "none" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="default" color="inherit">
        settings
      </Icon>
    </ArgonBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={dynamicBrand}
              brandName="Edushala"
              routes={filterRoutes(routes)} // ✅ filtered routes
              // routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
        <ToastContainer position="top-right" autoClose={3000} />
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={dynamicBrand}
            brandName=""
            routes={filterRoutes(routes)} // ✅ filtered routes
            // routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}

      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
      {/* ✅ Toast container yaha add karo */}
      <ToastContainer position="top-right" autoClose={3000} />
    </ThemeProvider>
  );
}
